/**
 * Utilitários para geração de URLs SEO-friendly de imóveis.
 *
 * Formato: /imovel/AP156-apartamento-4-quartos-venda-199m2-serrinha-goiania-go
 */

import { PROPERTY_TYPE_VALUES } from '@/lib/property-types';

// Tokens slugificados derivados da lista canônica + 'imovel' (token genérico legado)
const PROPERTY_TYPE_SLUGS: string[] = Array.from(
  new Set([...PROPERTY_TYPE_VALUES.map((v) => slugify(v)), 'imovel'])
);

/** Remove acentos e caracteres especiais, converte para lowercase com hífens. */
export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export type PropertyForSlug = {
  property_code?: string | null;
  property_type?: string | null;
  bedrooms?: number | null;
  transaction_type?: string | null;
  built_area?: { toNumber(): number } | number | string | null;
  total_area?: { toNumber(): number } | number | string | null;
  area_unit?: string | null;
  built_area_unit?: string | null;
  building_name?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

/**
 * Gera o slug SEO-friendly para um imóvel.
 *
 * @example
 * buildPropertySlug(imovel) // "AP156-apartamento-4-quartos-venda-199m2-serrinha-goiania-go"
 */
export function buildPropertySlug(imovel: PropertyForSlug): string {
  // Garantir que o código não contenha caracteres que quebrem o roteamento Next.js
  const rawCode = imovel.property_code || '';
  const code = rawCode
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  const parts: string[] = [code];

  if (imovel.property_type) {
    parts.push(slugify(imovel.property_type));
  }

  if (imovel.bedrooms && imovel.bedrooms > 0) {
    parts.push(`${imovel.bedrooms}-quartos`);
  }

  if (imovel.transaction_type) {
    parts.push(slugify(imovel.transaction_type));
  }

  const areaRaw = imovel.built_area || imovel.total_area;
  const areaUnit = (imovel.built_area ? imovel.built_area_unit : imovel.area_unit) || 'm2';
  if (areaRaw && Number(areaRaw) > 0) {
    const areaM2 = Number(areaRaw);
    if (areaUnit === 'ha') {
      const ha = Math.round(areaM2 / 10000);
      parts.push(`${ha}ha`);
    } else if (areaUnit === 'alq') {
      const alq = Math.round(areaM2 / 48400);
      parts.push(`${alq}alq`);
    } else {
      parts.push(`${Math.round(areaM2)}m2`);
    }
  }

  if (imovel.building_name) {
    parts.push(slugify(imovel.building_name));
  }

  if (imovel.neighborhood) {
    parts.push(slugify(imovel.neighborhood));
  }

  if (imovel.city) {
    parts.push(slugify(imovel.city));
  }

  if (imovel.state) {
    parts.push(slugify(imovel.state));
  }

  return parts.filter(Boolean).join('-');
}

/**
 * Gera o path completo para link interno.
 *
 * @example
 * buildPropertyUrl(imovel) // "/imovel/AP156-apartamento-4-quartos-venda-199m2-serrinha-goiania-go"
 */
export function buildPropertyUrl(imovel: PropertyForSlug): string {
  return `/imovel/${buildPropertySlug(imovel)}`;
}

/**
 * URL base absoluta do tenant (domínio personalizado ou subdomínio do sistema).
 * Mesma lógica do sitemap — ignora domínios localhost para gerar links públicos.
 */
export function buildTenantBaseUrl(
  tenant: { domain?: string | null; subdomain?: string | null } | null | undefined
): string {
  if (!tenant) return '';
  if (tenant.domain && !tenant.domain.includes('localhost')) return `https://${tenant.domain}`;
  if (tenant.subdomain) return `https://${tenant.subdomain}.porttal360.com.br`;
  return '';
}

/**
 * URL pública absoluta de um imóvel (base do tenant + path SEO-friendly).
 * Ex.: https://wesleyreis.com.br/imovel/AP016-apartamento-2-quartos-...
 */
export function buildAbsolutePropertyUrl(
  imovel: PropertyForSlug,
  tenant: { domain?: string | null; subdomain?: string | null } | null | undefined
): string {
  return `${buildTenantBaseUrl(tenant)}${buildPropertyUrl(imovel)}`;
}

/**
 * Ocorrência mais próxima do início de `-{token}` seguida de `-` ou fim da string.
 * A fronteira evita falsos positivos como '-lote' dentro de '-loteamento' ou
 * '-vila' dentro de '-vilamar'; o menor índice garante que o token do tipo
 * (logo após o código) vence palavras de tipo em bairros (ex.: 'setor-vila-rosa').
 * Em empate de índice, o token mais longo vence ('casa-em-condominio' > 'casa').
 */
function findTypeToken(lowerSlug: string): { index: number; token: string } | null {
  let bestIdx = -1;
  let bestToken = '';
  for (const token of PROPERTY_TYPE_SLUGS) {
    const needle = `-${token}`;
    let from = 0;
    while (true) {
      const idx = lowerSlug.indexOf(needle, from);
      if (idx === -1) break;
      if (idx === 0) { from = 1; continue; } // sem código antes do tipo
      const after = idx + needle.length;
      if (after === lowerSlug.length || lowerSlug[after] === '-') {
        if (
          bestIdx === -1 ||
          idx < bestIdx ||
          (idx === bestIdx && token.length > bestToken.length)
        ) {
          bestIdx = idx;
          bestToken = token;
        }
        break; // primeira ocorrência válida deste token já é a menor
      }
      from = idx + 1; // fronteira inválida — segue procurando
    }
  }
  return bestIdx > 0 ? { index: bestIdx, token: bestToken } : null;
}

/**
 * Extrai o property_code de um slug SEO-friendly ou de um código puro.
 *
 * @example
 * extractPropertyCode("AP156") // "AP156"
 * extractPropertyCode("AP156-apartamento-4-quartos-venda") // "AP156"
 * extractPropertyCode("CS-001-casa-3-quartos") // "CS-001"
 */
export function extractPropertyCode(slugOrCode: string): string {
  if (!slugOrCode.includes('-')) {
    return slugOrCode;
  }

  const match = findTypeToken(slugOrCode.toLowerCase());
  if (match) {
    return slugOrCode.substring(0, match.index);
  }

  // Nenhum tipo de imóvel encontrado — pode ser um property_code com hífen
  return slugOrCode;
}

export interface SlugCriteria {
  propertyType?: string;
  bedrooms?: number;
  transactionType?: 'venda' | 'aluguel';
  /** Trecho do slug após o tipo — bairro/cidade são resolvidos no servidor por matching contra o banco. */
  locationSlugTail?: string;
}

/**
 * Extrai critérios de busca de um slug de imóvel (possivelmente quebrado/antigo),
 * para sugerir imóveis similares na página 404.
 *
 * Bairro/cidade NÃO são separados por posição (building_name vs bairro é ambíguo;
 * cidades multi-palavra quebram heurísticas posicionais) — devolve locationSlugTail
 * para resolução contra valores reais do banco.
 *
 * @example
 * parseSlugCriteria("AP035-apartamento-3-quartos-venda-86m2-evidence-ville-alto-da-gloria-goiania-go")
 * // { propertyType: 'apartamento', bedrooms: 3, transactionType: 'venda',
 * //   locationSlugTail: '3-quartos-venda-86m2-evidence-ville-alto-da-gloria-goiania-go' }
 */
export function parseSlugCriteria(slugOrPath: string): SlugCriteria {
  const slug = slugOrPath.toLowerCase();
  const criteria: SlugCriteria = {};

  const match = findTypeToken(slug);

  if (match && match.token !== 'imovel') {
    criteria.propertyType = match.token;
  }

  const bedroomsMatch = slug.match(/-(\d+)-quartos?(-|$)/);
  if (bedroomsMatch) {
    criteria.bedrooms = parseInt(bedroomsMatch[1], 10);
  }

  const transactionMatch = slug.match(/-(venda|aluguel)(-|$)/);
  if (transactionMatch) {
    criteria.transactionType = transactionMatch[1] as 'venda' | 'aluguel';
  }

  if (match) {
    const tail = slug.substring(match.index + match.token.length + 1).replace(/^-+/, '');
    if (tail) {
      criteria.locationSlugTail = tail;
    }
  }

  return criteria;
}
